import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { CACHE_TIMES } from "../../query/options";
import { useModuleEnabled } from "../gym/useModuleEnabled";
import { getGatewayProviderCapabilitiesRequest } from "../requests/gateway";
import type { GatewayProviderCapabilities } from "../../utils/zod/gatewayProviders";
import {
  GATEWAY_PROVIDERS,
  type GatewayProviderMeta,
} from "../../constants/gatewayProviders";

export const GATEWAY_PROVIDERS_QUERY_KEY = ["gatewayProviders"] as const;

/** Lo que el backend dice que soporta una pasarela. */
interface Capabilities {
  available: boolean;
  subscriptionsAvailable: boolean;
  pausableSubscriptions: boolean;
  oauthAvailable: boolean;
  disputesAvailable: boolean;
}

/** Una pasarela con su presentacion del cliente y sus capacidades del backend. */
export type GatewayProvider = GatewayProviderMeta & Capabilities;

/**
 * Lo que se muestra mientras la consulta no resolvio, o si falla.
 *
 * No es un detalle: si esto quedara vacio, el gym no podria elegir pasarela y
 * por lo tanto no podria cobrar.
 *
 * Cada valor se elige por su consecuencia si esta equivocado, no por lo que
 * suele ser cierto. Por eso oauthAvailable cae en false aunque MercadoPago casi
 * siempre lo soporte: depende de si el DEPLOY tiene las credenciales de la
 * aplicacion, que el cliente no puede saber. Suponer que si ofrece un boton de
 * "conectar con un click" que responde 503; suponer que no deja al gym con el
 * asistente manual, que funciona igual.
 */
const FALLBACK: Record<string, Capabilities> = {
  mercadopago: {
    available: true,
    subscriptionsAvailable: true,
    pausableSubscriptions: true,
    oauthAvailable: false,
    disputesAvailable: true,
  },
  flow: {
    available: true,
    subscriptionsAvailable: true,
    // Flow no tiene pausa: solo da de baja.
    pausableSubscriptions: false,
    oauthAvailable: false,
    disputesAvailable: false,
  },
};

const NOT_INTEGRATED: Capabilities = {
  available: false,
  subscriptionsAvailable: false,
  pausableSubscriptions: false,
  oauthAvailable: false,
  disputesAvailable: false,
};

function merge(
  capabilities: GatewayProviderCapabilities[] | undefined,
): GatewayProvider[] {
  const byProvider = new Map(capabilities?.map((c) => [c.provider, c]));

  return GATEWAY_PROVIDERS.map((meta) => {
    const remote = byProvider.get(meta.key);

    if (remote) {
      return {
        ...meta,
        available: remote.available,
        subscriptionsAvailable: remote.subscriptions,
        pausableSubscriptions: remote.pausableSubscriptions,
        oauthAvailable: remote.oauth,
        disputesAvailable: remote.disputes,
      };
    }

    // Sin respuesta del backend se usa lo de siempre. Una pasarela que el
    // backend no nombra es una que no esta integrada.
    return { ...meta, ...(FALLBACK[meta.key] ?? NOT_INTEGRATED) };
  });
}

/**
 * Las pasarelas con sus capacidades, listas para mostrar.
 *
 * Nunca devuelve una lista vacia: mientras la consulta no resuelve, o si falla,
 * caen los valores por defecto. Cachea 24 h porque son capacidades de la
 * plataforma y cambian con un deploy.
 */
export function useGatewayProviders(): {
  providers: GatewayProvider[];
  isLoading: boolean;
} {
  const { token } = useAuth();
  // El endpoint esta gateado por el modulo, y el modulo viene apagado por
  // defecto. Sin esto, abrir el formulario de un gasto o de una mantencion en
  // un gym sin pagos online dispara un 403 y un toast que dice "no tienes
  // permiso" — que ademas es falso: el modulo esta apagado, no falta permiso.
  const onlinePaymentsEnabled = useModuleEnabled("onlinePayments");

  const { data, isLoading } = useQuery({
    queryKey: GATEWAY_PROVIDERS_QUERY_KEY,
    queryFn: () => {
      if (!token) throw new Error("Token is required");
      return getGatewayProviderCapabilitiesRequest(token);
    },
    enabled: Boolean(token) && onlinePaymentsEnabled,
    // Este hook siempre tiene con que responder: un fallo se resuelve solo con
    // los valores por defecto y no necesita avisarle a nadie.
    meta: { skipGlobalErrorToast: true },
    ...CACHE_TIMES.STATIC_DATA,
  });

  // Memoizado porque merge() construye objetos nuevos: sin esto `providers`
  // cambia de identidad en cada render y los useMemo de quien lo consume nunca
  // se saltean.
  const providers = useMemo(() => merge(data), [data]);

  return { providers, isLoading };
}
